import React, { useEffect, useRef, forwardRef } from 'react';
import { StyledSlider, StyledOuterContainer, StyledTextContent, StyledLabel, StyledAppendix, StyledOutput, StyledMinMax, StyledFormMessage, StyledOutputContainer, StyledTrack, StyledSliderContainer, StyledControlContainer, StyledDisplayContainer, StyledIconButtonWrapper } from './index.styles.js';
import { IconButton } from '../icon-button/index.js';
export const Slider = forwardRef(({ id, value, label, appendix, hint, error, disabled, invalid, onChange, step = 1, list, max, min, hiddenValues, rangeSeparator = 'to', buttonControl, 'aria-describedby': ariaDescribedBy, 'aria-label': ariaLabel, valueFormat }, ref) => {
    const containerRef = useRef(null);
    const outputRef = useRef(null);
    const valueIsArray = Array.isArray(value);
    useEffect(() => {
        if (containerRef.current) {
            if (valueIsArray) {
                containerRef.current.style.setProperty('--value0', value[0].toString());
                containerRef.current.style.setProperty('--value1', value[1].toString());
                containerRef.current.style.setProperty('--start', `var(${value[0] < value[1] ? '--position0' : '--position1'})`);
                containerRef.current.style.setProperty('--end', `var(${value[0] > value[1] ? '--position0' : '--position1'})`);
            }
            else {
                containerRef.current.style.setProperty('--value0', value.toString());
            }
            containerRef.current.style.setProperty('--min', min.toString());
            containerRef.current.style.setProperty('--max', max.toString());
        }
    }, [value, valueIsArray, min, max]);
    useEffect(() => {
        const injectOutputSize = () => {
            if (containerRef.current && outputRef.current) {
                containerRef.current.style.setProperty('--output-size', `${outputRef.current.clientWidth}px`);
            }
        };
        if (document.fonts) {
            document.fonts.ready.then(() => {
                injectOutputSize();
            });
        }
    }, [value]);
    let newValue;
    const handleChange = (event, thumbIndex) => {
        const nextValue = Number(event.target.value);
        if (valueIsArray) {
            newValue =
                thumbIndex === 0 ? [nextValue, value[1]] : [value[0], nextValue];
        }
        else {
            newValue = nextValue;
        }
        if (onChange) {
            onChange(newValue, event);
        }
    };
    const decreaseValue = (thumbIndex) => {
        let newValue;
        if (valueIsArray) {
            newValue =
                thumbIndex === 0
                    ? [Math.max(value[0] - step, min), value[1]]
                    : [value[0], Math.max(value[1] - step, min)];
        }
        else {
            newValue = Math.max(value - step, min);
        }
        if (onChange) {
            const event = new Event('input', {
                bubbles: true
            });
            onChange(newValue, event);
        }
    };
    const increaseValue = (thumbIndex) => {
        let newValue;
        if (valueIsArray) {
            newValue =
                thumbIndex === 0
                    ? [Math.min(value[0] + step, max), value[1]]
                    : [value[0], Math.min(value[1] + step, max)];
        }
        else {
            newValue = Math.min(value + step, max);
        }
        if (onChange) {
            const event = new Event('input', {
                bubbles: true
            });
            onChange(newValue, event);
        }
    };
    const dataListId = `${id}__datalist`;
    const sliderLabel = `${id}__label`;
    const renderButtonControl = (_thumbIndex, ariaLabel, icon, onClick) => (React.createElement(IconButton, { size: "md", variant: "tertiary", icon: icon, onClick: onClick, disabled: disabled, "aria-label": ariaLabel }));
    return (React.createElement(StyledOuterContainer, { ref: containerRef, valueIsArray: valueIsArray },
        label && (React.createElement(StyledTextContent, null,
            React.createElement(StyledLabel, { id: sliderLabel, disabled: disabled, invalid: invalid }, label),
            React.createElement(StyledAppendix, null, appendix))),
        React.createElement(StyledControlContainer, null,
            buttonControl && (React.createElement(StyledIconButtonWrapper, null,
                valueIsArray &&
                    renderButtonControl(1, buttonControl.defaultSliderAriaLabel.decrease, 'add-circle', () => increaseValue(0)),
                renderButtonControl(0, buttonControl.additionalSliderAriaLabel.decrease, 'remove-circle', () => decreaseValue(0)))),
            React.createElement(StyledDisplayContainer, null,
                !hiddenValues && (React.createElement(StyledOutputContainer, { valueIsArray: valueIsArray },
                    React.createElement(StyledOutput, { valueIsArray: valueIsArray, ref: outputRef, disabled: disabled, invalid: invalid, rangeSeparator: rangeSeparator }, valueIsArray ? (React.createElement(React.Fragment, null,
                        [...value].sort((a, b) => a - b)[0],
                        value[1] !== undefined && (React.createElement(React.Fragment, null,
                            React.createElement("span", null,
                                " ",
                                rangeSeparator,
                                " "),
                            [...value].sort((a, b) => a - b)[1])))) : (value)))),
                list && (React.createElement("datalist", { id: dataListId }, list.map((entry) => {
                    const [listValue, label] = Array.isArray(entry)
                        ? entry
                        : [entry];
                    return (React.createElement("option", { key: listValue, value: listValue, label: label }));
                }))),
                React.createElement(StyledSliderContainer, null,
                    React.createElement(StyledTrack, { valueIsArray: valueIsArray, disabled: disabled, invalid: invalid, marks: list ? list : [] }),
                    React.createElement(StyledSlider, { id: `${id}-0`, ref: ref, type: "range", min: min, max: max, step: step, value: valueIsArray ? value[0] : value, onChange: (e) => handleChange(e, 0), disabled: disabled, invalid: invalid, "aria-valuemin": min, "aria-valuemax": max, "aria-valuenow": valueIsArray ? value[0] : value, "aria-valuetext": valueFormat
                            ? valueFormat(valueIsArray ? value[0] : value)
                            : (valueIsArray ? value[0] : value).toString(), "aria-describedby": ariaDescribedBy, "aria-labelledby": sliderLabel, "aria-label": ariaLabel, list: list ? dataListId : undefined }),
                    valueIsArray && value[1] !== undefined && (React.createElement(StyledSlider, { id: `${id}-1`, ref: ref, type: "range", min: min, max: max, step: step, value: value[1], onChange: (e) => handleChange(e, 1), disabled: disabled, invalid: invalid, role: "slider", "aria-valuemin": min, "aria-valuemax": max, "aria-valuenow": value[1], "aria-valuetext": valueFormat ? valueFormat(value[1]) : value[1].toString(), "aria-describedby": ariaDescribedBy, "aria-labelledby": sliderLabel, "aria-label": ariaLabel, list: list ? dataListId : undefined }))),
                !hiddenValues && (React.createElement(StyledMinMax, null,
                    React.createElement("span", null, min),
                    React.createElement("span", null, max)))),
            buttonControl && (React.createElement(StyledIconButtonWrapper, null, !valueIsArray ? (renderButtonControl(0, buttonControl.defaultSliderAriaLabel.increase, 'add-circle', () => increaseValue(0))) : (React.createElement(React.Fragment, null,
                renderButtonControl(1, buttonControl.additionalSliderAriaLabel.increase, 'add-circle', () => increaseValue(1)),
                renderButtonControl(0, buttonControl.additionalSliderAriaLabel.decrease, 'remove-circle', () => decreaseValue(1))))))),
        hint && (React.createElement(StyledFormMessage, { text: hint, variant: "hint", id: "messageID" })),
        invalid && !disabled && (React.createElement(StyledFormMessage, { text: error, variant: "error", id: "errorID" }))));
});
Slider.displayName = 'Slider';
//# sourceMappingURL=index.js.map